import { useState, useCallback, useEffect, useMemo } from 'react';
import { IProposta } from '../utility/IProposta';
import { compareObjects, hasChanges as hasChangesUtil, MudancaCampo } from '../utils/compareData';
import { validateAllFields, validateCep, validateProcesso } from '../utils/validations';
import { sanitizeErrorMessage } from '../utils/errorSanitizer';
import { 
  validateDateNotPast, 
  validatePrazoFatalComplete,
  validatePrazoAceiteComPrazoFatal,
  validatePrazoConclusaoComPrazoFatal
} from '../utils/dateValidations';
import { convertPartesProcesso, convertPartesRepresentadas, ParteProcessoFormatted } from '../utils/partesConverter';
import api from '../services/api';
import toast from 'react-hot-toast';
import { toastDefs, toastDefsError } from '../config/toastConfig';

type CurrencyValue = string | number | null | undefined;

const convertCurrencyToNumber = (value: CurrencyValue): number | null => {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value === 'number') return value;
  if (typeof value === 'string') {
    const cleaned = value.replace(/[^\d,.-]/g, '').replace(',', '.');
    const parsed = parseFloat(cleaned);
    return isNaN(parsed) ? null : parsed;
  }
  return null;
};

type DateValue = Date | string | null | undefined;

const formatDateForBackend = (date: DateValue): string | null => {
  if (!date) return null;
  
  try {
    if (date instanceof Date) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      const seconds = String(date.getSeconds()).padStart(2, '0');
      return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
    }
    
    if (typeof date === 'string') {
      if (date.includes('T') && !date.endsWith('Z')) {
        return date;
      }
      if (date.includes('T') && date.endsWith('Z')) {
        const dateObj = new Date(date);
        if (!isNaN(dateObj.getTime())) {
          const year = dateObj.getFullYear();
          const month = String(dateObj.getMonth() + 1).padStart(2, '0');
          const day = String(dateObj.getDate()).padStart(2, '0');
          const hours = String(dateObj.getHours()).padStart(2, '0');
          const minutes = String(dateObj.getMinutes()).padStart(2, '0');
          const seconds = String(dateObj.getSeconds()).padStart(2, '0');
          return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
        }
      }
      const dateObj = new Date(date);
      if (!isNaN(dateObj.getTime())) {
        const year = dateObj.getFullYear();
        const month = String(dateObj.getMonth() + 1).padStart(2, '0');
        const day = String(dateObj.getDate()).padStart(2, '0');
        const hours = String(dateObj.getHours()).padStart(2, '0');
        const minutes = String(dateObj.getMinutes()).padStart(2, '0');
        const seconds = String(dateObj.getSeconds()).padStart(2, '0');
        return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
      }
    }
  } catch (e) {
    console.error('Erro ao formatar data:', e, date);
  }
  
  return null;
};

interface ApiError {
  response?: {
    data?: {
      result?: Array<{ field?: string; message?: string }>;
      message?: string;
      error?: string;
    };
  };
  message?: string;
}

const parseErrorResponse = (error: unknown): { rawErrorMessage: string; fieldErrors: Record<string, string> } => {
  const apiError = error as ApiError;
  let rawErrorMessage = 'Ocorreu um erro ao salvar as alterações.';
  const fieldErrors: Record<string, string> = {};

  if (apiError.response) {
    const responseData = apiError.response.data;
    
    console.error('Dados da resposta de erro:', responseData);
    
    if (responseData?.result && Array.isArray(responseData.result)) {
      responseData.result.forEach((err) => {
        if (err.field && err.message) {
          const sanitizedMessage = sanitizeErrorMessage(err.message);
          fieldErrors[err.field] = sanitizedMessage;
        } else if (err.message) {
          rawErrorMessage = err.message;
        }
      });
    } else if (responseData?.message) {
      rawErrorMessage = responseData.message;
    } else if (responseData?.error) {
      rawErrorMessage = responseData.error;
    }
  } else if (apiError.message) {
    rawErrorMessage = apiError.message;
  }

  if (rawErrorMessage && !Object.keys(fieldErrors).length) {
    const errorLower = rawErrorMessage.toLowerCase();
    const isComarcaError = errorLower.includes('id_comarca') || 
                          errorLower.includes('fk_proposta_comarca') || 
                          (errorLower.includes('comarca') && errorLower.includes('foreign key'));
    
    if (isComarcaError) {
      fieldErrors.id_comarca = 'Comarca é obrigatória';
    }
  }

  return { rawErrorMessage, fieldErrors };
};

const isStatusFinalizadoOuCancelado = (demanda: IProposta | null | undefined): boolean => {
  if (!demanda || !demanda.status) return false;
  const status = demanda.status.toLowerCase();
  return status === 'finalizada' || status === 'cancelada';
};

export const isStatusOndePrazoAceiteNaoEditavel = (demanda: IProposta | null | undefined): boolean => {
  if (!demanda || !demanda.status) return false;
  const status = demanda.status.toLowerCase();
  return status === 'aguardando execução' || 
         status === 'aguardando execucao' ||
         status === 'em execução' || 
         status === 'em execucao' ||
         status === 'em conferência' || 
         status === 'em conferencia' ||
         status === 'finalizada' || 
         status === 'cancelada';
};

interface ProposalPayload {
  numero_processo: string | null;
  nome_cliente: string | null;
  valor_cliente: number | null;
  codigo_cliente: string | null;
  prazo_aceite: string | null;
  prazo_para_conclusao: string | null;
  dias_uteis_para_conclusao: string | null;
  orientacoes: string | null;
  tipo_audiencia: string | null;
  data: string | null;
  data_tz: string | null;
  profissional_necessario: string | null;
  modalidade: string | null;
  endereco_virtual: string | null;
  rua: string | null;
  bairro: string | null;
  cep: string | null;
  numero: string | null;
  complemento: string | null;
  valor_profissional: number | null;
  natureza: string | null;
  id_servico: number | null;
  id_comarca: number | null;
  id_usuario: number | null;
  partes_processo: ParteProcessoFormatted[];
  partes_representadas: string[];
}

const buildProposalPayload = (
  dadosEditados: IProposta, 
  dadosOriginais: IProposta,
  partesProcesso: ParteProcessoFormatted[], 
  partesRepresentadas: string[],
  changes: Record<string, MudancaCampo>,
  demanda?: IProposta | null
): ProposalPayload => {
  const getDateValue = (campo: string): string | null => {
    const valorEditado = dadosEditados[campo as keyof IProposta];
    const valorOriginal = dadosOriginais[campo as keyof IProposta];
    
    if (changes[campo]) {
      return formatDateForBackend(valorEditado as DateValue);
    }
    
    if (!valorOriginal) {
      return null;
    }
    
    return formatDateForBackend(valorOriginal as DateValue);
  };

  const isRestrito = isStatusFinalizadoOuCancelado(demanda || dadosOriginais);

  const payload: ProposalPayload = {
    numero_processo: dadosEditados.numero_processo || null,
    nome_cliente: dadosEditados.nome_cliente || null,
    valor_cliente: convertCurrencyToNumber(dadosEditados.valor_cliente),
    codigo_cliente: dadosEditados.codigo_cliente || null,
    prazo_aceite: getDateValue('prazo_aceite'),
    prazo_para_conclusao: getDateValue('prazo_para_conclusao'),
    dias_uteis_para_conclusao: dadosEditados.dias_uteis_para_conclusao || null,
    orientacoes: dadosEditados.orientacoes || null,
    tipo_audiencia: dadosEditados.tipo_audiencia || null,
    data: getDateValue('data'),
    data_tz: dadosEditados.data_tz || null,
    profissional_necessario: dadosEditados.profissional_necessario || null,
    modalidade: dadosEditados.modalidade || null,
    endereco_virtual: dadosEditados.endereco_virtual || null,
    rua: dadosEditados.rua || null,
    bairro: dadosEditados.bairro || null,
    cep: dadosEditados.cep || null,
    numero: dadosEditados.numero || null,
    complemento: dadosEditados.complemento || null,
    valor_profissional: convertCurrencyToNumber(dadosEditados.valor_profissional),
    natureza: dadosEditados.natureza || null,
    id_servico: (dadosEditados.id_servico !== undefined && dadosEditados.id_servico !== null) 
      ? Number(dadosEditados.id_servico) 
      : (dadosOriginais.id_servico ? Number(dadosOriginais.id_servico) : null),
    id_comarca: dadosEditados.id_comarca ? Number(dadosEditados.id_comarca) : null,
    id_usuario: dadosOriginais.id_usuario ? Number(dadosOriginais.id_usuario) : null,
    partes_processo: partesProcesso,
    partes_representadas: partesRepresentadas,
  };

  if (isRestrito) {
    const camposPermitidos: (keyof ProposalPayload)[] = ['valor_cliente', 'valor_profissional'];
    const payloadFiltrado: ProposalPayload = {
      numero_processo: null,
      nome_cliente: null,
      codigo_cliente: null,
      prazo_aceite: null,
      prazo_para_conclusao: null,
      dias_uteis_para_conclusao: null,
      orientacoes: null,
      tipo_audiencia: null,
      data: null,
      data_tz: null,
      profissional_necessario: null,
      modalidade: null,
      endereco_virtual: null,
      rua: null,
      bairro: null,
      cep: null,
      numero: null,
      complemento: null,
      natureza: null,
      id_servico: null,
      id_comarca: null,
      id_usuario: null,
      partes_processo: [],
      partes_representadas: [],
      valor_cliente: payload.valor_cliente,
      valor_profissional: payload.valor_profissional,
    };
    return payloadFiltrado;
  }

  return payload;
};

const validateEdicaoDemanda = (
  dadosEditados: IProposta,
  demanda: IProposta
): Record<string, string> => {
  const validationErrors: Record<string, string> = {};
  const isRestrito = isStatusFinalizadoOuCancelado(demanda);

  if (isRestrito) {
    const valorClienteNumero = convertCurrencyToNumber(dadosEditados.valor_cliente);
    if (valorClienteNumero === null || valorClienteNumero === undefined) {
      validationErrors.valor_cliente = 'Valor do Cliente é obrigatório';
    } else if (valorClienteNumero <= 0) {
      validationErrors.valor_cliente = 'Valor do Cliente deve ser maior que zero';
    }

    const valorProfissionalNumero = convertCurrencyToNumber(dadosEditados.valor_profissional);
    if (valorProfissionalNumero === null || valorProfissionalNumero === undefined) {
      validationErrors.valor_profissional = 'Valor do Profissional é obrigatório';
    } else if (valorProfissionalNumero <= 0) {
      validationErrors.valor_profissional = 'Valor do Profissional deve ser maior que zero';
    }

    return validationErrors;
  }

  const requiredFields: string[] = [];
  
  if (demanda.descricao_categoria_servico === 'Audiências') {
    requiredFields.push('tipo_audiencia', 'modalidade', 'profissional_necessario');
  }
  
  requiredFields.push('numero_processo', 'valor_cliente', 'valor_profissional', 'data');
  
  if (!dadosEditados.natureza) {
    validationErrors.natureza = 'Natureza é obrigatória';
  }

  const validation = validateAllFields(dadosEditados, requiredFields);
  if (!validation.valid) {
    Object.assign(validationErrors, validation.errors);
  }

  const valorClienteNumero = convertCurrencyToNumber(dadosEditados.valor_cliente);
  if (valorClienteNumero !== null && valorClienteNumero <= 0 && !validationErrors.valor_cliente) {
    validationErrors.valor_cliente = 'Valor do Cliente deve ser maior que zero';
  }

  const valorProfissionalNumero = convertCurrencyToNumber(dadosEditados.valor_profissional);
  if (valorProfissionalNumero !== null && valorProfissionalNumero <= 0 && !validationErrors.valor_profissional) {
    validationErrors.valor_profissional = 'Valor do Profissional deve ser maior que zero';
  }

  if (dadosEditados.cep && !validateCep(dadosEditados.cep)) {
    validationErrors.cep = 'CEP deve ter 8 dígitos no formato 00000-000';
  }

  if (dadosEditados.numero_processo) {
    if (dadosEditados.natureza === 'Judicial' && !validateProcesso(dadosEditados.numero_processo)) {
      validationErrors.numero_processo = 'Número do processo deve ter 20 dígitos no formato CNJ (0000000-00.0000.0.00.0000)';
    }
  }

  if (!dadosEditados.titulo_servico) {
    validationErrors.titulo_servico = 'Área do Direito é obrigatória';
  }

  if (!dadosEditados.id_comarca) {
    validationErrors.id_comarca = 'Comarca é obrigatória';
  }

  const prazoAceiteNaoEditavel = isStatusOndePrazoAceiteNaoEditavel(demanda);
  if (!prazoAceiteNaoEditavel) {
    const prazoAceiteError = validateDateNotPast(dadosEditados.prazo_aceite, 'Data/hora de aceite');
    if (prazoAceiteError) {
      validationErrors.prazo_aceite = prazoAceiteError;
    }

    const prazoAceiteComFatalError = validatePrazoAceiteComPrazoFatal(
      dadosEditados.prazo_aceite,
      dadosEditados.data
    );
    if (prazoAceiteComFatalError) {
      validationErrors.prazo_aceite = prazoAceiteComFatalError;
    }
  }

  const prazoConclusaoError = validateDateNotPast(dadosEditados.prazo_para_conclusao, 'Data/hora de conclusão');
  if (prazoConclusaoError) {
    validationErrors.prazo_para_conclusao = prazoConclusaoError;
  }

  const prazoConclusaoComFatalError = validatePrazoConclusaoComPrazoFatal(
    dadosEditados.prazo_para_conclusao,
    dadosEditados.data
  );
  if (prazoConclusaoComFatalError) {
    validationErrors.prazo_para_conclusao = prazoConclusaoComFatalError;
  }

  const prazoFatalErrors = validatePrazoFatalComplete(
    dadosEditados.data,
    demanda?.criado
  );
  Object.assign(validationErrors, prazoFatalErrors);

  if (dadosEditados.modalidade === 'Virtual' && (!dadosEditados.endereco_virtual || dadosEditados.endereco_virtual.trim() === '')) {
    validationErrors.endereco_virtual = 'Endereço virtual é obrigatório quando a modalidade é Virtual';
  }

  if (dadosEditados.modalidade === 'online' && (!dadosEditados.endereco_virtual || dadosEditados.endereco_virtual.trim() === '')) {
    validationErrors.endereco_virtual = 'Endereço virtual é obrigatório quando a modalidade é Virtual';
  }

  return validationErrors;
};

interface UseEdicaoDemandaReturn {
  isEditing: boolean;
  dadosOriginais: IProposta | null;
  dadosEditados: IProposta | null;
  iniciarEdicao: () => void;
  cancelarEdicao: () => void;
  salvarEdicao: () => Promise<void>;
  hasChanges: () => boolean;
  atualizarDadosEditados: (campo: string, valor: unknown) => void;
  validateField: (campo: string) => void;
  errors: Record<string, string>;
  isValidating: boolean;
  isSaving: boolean;
  camposEditaveisRestritos: boolean;
}

export function useEdicaoDemanda(
  demanda: IProposta | null | undefined,
  onSaveSuccess?: (updatedDemanda: IProposta) => void
): UseEdicaoDemandaReturn {
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [dadosOriginais, setDadosOriginais] = useState<IProposta | null>(null);
  const [dadosEditados, setDadosEditados] = useState<IProposta | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isValidating, setIsValidating] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);

  useEffect(() => {
    if (demanda && !isEditing) {
      setDadosOriginais(demanda);
      setDadosEditados(null);
      setErrors({});
    }
  }, [demanda, isEditing]);

  const iniciarEdicao = useCallback(() => {
    if (demanda) {
      const dadosBackup = JSON.parse(JSON.stringify(demanda));
      setDadosOriginais(dadosBackup);
      setDadosEditados(dadosBackup);
      setErrors({});
      setIsEditing(true);
    }
  }, [demanda]);

  const cancelarEdicao = useCallback(() => {
    setDadosEditados(null);
    setErrors({});
    setIsEditing(false);
  }, []);

  const salvarEdicao = useCallback(async () => {
    if (!dadosEditados || !dadosOriginais || !demanda?.id_proposta) {
      return;
    }

    setIsValidating(true);
    setIsSaving(true);
    setErrors({});

    const validationErrors = validateEdicaoDemanda(dadosEditados, demanda);

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      setIsValidating(false);
      setIsSaving(false);
      
      const errorKeys = Object.keys(validationErrors);
      const errorMessages = Object.values(validationErrors);
      
      if (errorKeys.length === 1) {
        toast.error(errorMessages[0], toastDefsError);
      } else {
        const errorList = errorMessages.slice(0, 5).join(', ');
        const remainingCount = errorKeys.length > 5 ? ` e mais ${errorKeys.length - 5} erro(s)` : '';
        toast.error(`Preencha todos os campos obrigatórios: ${errorList}${remainingCount}`, toastDefsError);
      }
      return;
    }

    try {
      const changes = compareObjects(dadosOriginais, dadosEditados);
      
      if (Object.keys(changes).length === 0) {
        setIsEditing(false);
        setIsValidating(false);
        setIsSaving(false);
        return;
      }

      const partesProcessoRaw = dadosEditados.partes_processo || [];
      const partesRepresentadasRaw = dadosEditados.partes_representadas || [];

      const partesProcesso = convertPartesProcesso(partesProcessoRaw);
      const partesRepresentadas = convertPartesRepresentadas(partesRepresentadasRaw, partesProcessoRaw);

      const proposalUpdated = buildProposalPayload(dadosEditados, dadosOriginais, partesProcesso, partesRepresentadas, changes, demanda);

      const response = await api.put(`proposta/${demanda.id_proposta}`, proposalUpdated);

      if (response.data.status !== 'success') {
        const rawErrorMessage = response.data.result?.[0]?.message || response.data.message || 'Ocorreu um erro ao salvar as alterações.';
        console.error('Erro na resposta da API:', {
          status: response.data.status,
          message: rawErrorMessage,
          result: response.data.result,
          fullResponse: response.data
        });
        throw new Error(rawErrorMessage);
      }

      toast.success('Alterações salvas com sucesso.', toastDefs);
      
      setIsEditing(false);
      setErrors({});
      
      const updatedDemanda = response.data.result || dadosEditados;
      setDadosOriginais(updatedDemanda);
      setDadosEditados(null);
      
      if (onSaveSuccess) {
        onSaveSuccess(updatedDemanda);
      }
    } catch (error: unknown) {
      const apiError = error as ApiError;
      console.error('Erro ao salvar edição da demanda:', {
        error,
        response: apiError.response,
        message: apiError.message,
        stack: apiError instanceof Error ? apiError.stack : undefined,
        demandaId: demanda?.id_proposta
      });

      const { rawErrorMessage, fieldErrors } = parseErrorResponse(error);
      const userFriendlyMessage = sanitizeErrorMessage(rawErrorMessage);

      if (Object.keys(fieldErrors).length > 0) {
        setErrors(fieldErrors);
        const firstFieldError = Object.values(fieldErrors)[0];
        toast.error(firstFieldError || userFriendlyMessage, toastDefsError);
      } else {
        setErrors({ geral: userFriendlyMessage });
        toast.error(userFriendlyMessage, toastDefsError);
      }
    } finally {
      setIsValidating(false);
      setIsSaving(false);
    }
  }, [dadosEditados, dadosOriginais, demanda, onSaveSuccess]);

  const validateField = useCallback((campo: string) => {
    if (!dadosEditados || !demanda || !isEditing) {
      return;
    }

    const isRestrito = isStatusFinalizadoOuCancelado(demanda);
    if (isRestrito && campo !== 'valor_cliente' && campo !== 'valor_profissional') {
      return;
    }

    const fieldErrors: Record<string, string> = {};
    
    switch (campo) {
      case 'cep':
        if (dadosEditados.cep && !validateCep(dadosEditados.cep)) {
          fieldErrors.cep = 'CEP deve ter 8 dígitos no formato 00000-000';
        }
        break;
      case 'numero_processo':
        if (dadosEditados.numero_processo && dadosEditados.natureza === 'Judicial' && !validateProcesso(dadosEditados.numero_processo)) {
          fieldErrors.numero_processo = 'Número do processo deve ter 20 dígitos no formato CNJ (0000000-00.0000.0.00.0000)';
        }
        break;
      case 'natureza':
        if (!dadosEditados.natureza) {
          fieldErrors.natureza = 'Natureza é obrigatória';
        }
        break;
      case 'titulo_servico':
        if (!dadosEditados.titulo_servico) {
          fieldErrors.titulo_servico = 'Área do Direito é obrigatória';
        }
        break;
      case 'data':
        const prazoFatalErrors = validatePrazoFatalComplete(
          dadosEditados.data,
          demanda?.criado
        );
        if (prazoFatalErrors.data) {
          fieldErrors.data = prazoFatalErrors.data;
        }
        break;
      case 'prazo_aceite':
        const prazoAceiteNaoEditavel = isStatusOndePrazoAceiteNaoEditavel(demanda);
        if (!prazoAceiteNaoEditavel) {
          const prazoAceiteComFatalError = validatePrazoAceiteComPrazoFatal(
            dadosEditados.prazo_aceite,
            dadosEditados.data
          );
          if (prazoAceiteComFatalError) {
            fieldErrors.prazo_aceite = prazoAceiteComFatalError;
          }
        }
        break;
      case 'prazo_para_conclusao':
        const prazoConclusaoComFatalError = validatePrazoConclusaoComPrazoFatal(
          dadosEditados.prazo_para_conclusao,
          dadosEditados.data
        );
        if (prazoConclusaoComFatalError) {
          fieldErrors.prazo_para_conclusao = prazoConclusaoComFatalError;
        }
        break;
      case 'endereco_virtual':
        if ((dadosEditados.modalidade === 'Virtual' || dadosEditados.modalidade === 'online') && (!dadosEditados.endereco_virtual || dadosEditados.endereco_virtual.trim() === '')) {
          fieldErrors.endereco_virtual = 'Endereço virtual é obrigatório quando a modalidade é Virtual';
        }
        break;
      case 'tipo_audiencia':
      case 'modalidade':
      case 'profissional_necessario':
        if (demanda.descricao_categoria_servico === 'Audiências') {
          if (!dadosEditados.tipo_audiencia && campo === 'tipo_audiencia') {
            fieldErrors.tipo_audiencia = 'Tipo de Audiência é obrigatório';
          }
          if (!dadosEditados.modalidade && campo === 'modalidade') {
            fieldErrors.modalidade = 'Modalidade é obrigatória';
          }
          if (!dadosEditados.profissional_necessario && campo === 'profissional_necessario') {
            fieldErrors.profissional_necessario = 'Profissional Necessário é obrigatório';
          }
        }
        break;
      case 'valor_cliente':
      case 'valor_profissional':
        if (campo === 'valor_cliente') {
          if (!dadosEditados.valor_cliente) {
            fieldErrors.valor_cliente = 'Valor do Cliente é obrigatório';
          } else {
            const valorClienteNumero = convertCurrencyToNumber(dadosEditados.valor_cliente);
            if (valorClienteNumero !== null && valorClienteNumero <= 0) {
              fieldErrors.valor_cliente = 'Valor do Cliente deve ser maior que zero';
            }
          }
        }
        if (campo === 'valor_profissional') {
          if (!dadosEditados.valor_profissional) {
            fieldErrors.valor_profissional = 'Valor do Profissional é obrigatório';
          } else {
            const valorProfissionalNumero = convertCurrencyToNumber(dadosEditados.valor_profissional);
            if (valorProfissionalNumero !== null && valorProfissionalNumero <= 0) {
              fieldErrors.valor_profissional = 'Valor do Profissional deve ser maior que zero';
            }
          }
        }
        break;
    }

    setErrors((prevErrors) => {
      const newErrors = { ...prevErrors };
      if (fieldErrors[campo]) {
        newErrors[campo] = fieldErrors[campo];
      } else {
        delete newErrors[campo];
      }
      return newErrors;
    });
  }, [dadosEditados, demanda, isEditing]);

  const atualizarDadosEditados = useCallback((campo: string, valor: unknown) => {
    const isRestrito = isStatusFinalizadoOuCancelado(demanda);
    
    if (isRestrito && campo !== 'valor_cliente' && campo !== 'valor_profissional') {
      return;
    }

    const prazoAceiteNaoEditavel = isStatusOndePrazoAceiteNaoEditavel(demanda);
    if (prazoAceiteNaoEditavel && campo === 'prazo_aceite') {
      return;
    }
    
    setDadosEditados((prev) => {
      if (!prev) return null;
      
      const prevValue = prev[campo as keyof typeof prev];
      
      if (prevValue === valor) {
        return prev;
      }
      
      const updated = {
        ...prev,
        [campo]: valor,
      };
      
      setErrors((prevErrors) => {
        const newErrors = { ...prevErrors };
        
        if (errors[campo]) {
          delete newErrors[campo];
        }
        
        if (campo === 'data' && prev) {
          const prazoAceiteNaoEditavel = isStatusOndePrazoAceiteNaoEditavel(demanda);
          const prazoConclusaoComFatalError = validatePrazoConclusaoComPrazoFatal(
            updated.prazo_para_conclusao,
            valor as DateValue
          );
          
          if (!prazoAceiteNaoEditavel) {
            const prazoAceiteComFatalError = validatePrazoAceiteComPrazoFatal(
              updated.prazo_aceite,
              valor as DateValue
            );
            if (prazoAceiteComFatalError) {
              newErrors.prazo_aceite = prazoAceiteComFatalError;
            } else {
              delete newErrors.prazo_aceite;
            }
          } else {
            delete newErrors.prazo_aceite;
          }
          if (prazoConclusaoComFatalError) {
            newErrors.prazo_para_conclusao = prazoConclusaoComFatalError;
          } else {
            delete newErrors.prazo_para_conclusao;
          }
          
          delete newErrors.data;
        } else if (campo === 'prazo_aceite' && updated.data) {
          const prazoAceiteNaoEditavel = isStatusOndePrazoAceiteNaoEditavel(demanda);
          if (!prazoAceiteNaoEditavel) {
            const prazoAceiteComFatalError = validatePrazoAceiteComPrazoFatal(
              valor as DateValue,
              updated.data
            );
            
            if (prazoAceiteComFatalError) {
              newErrors.prazo_aceite = prazoAceiteComFatalError;
            } else {
              delete newErrors.prazo_aceite;
            }
          } else {
            delete newErrors.prazo_aceite;
          }
          
          delete newErrors.data;
        } else if (campo === 'prazo_para_conclusao' && updated.data) {
          const prazoConclusaoComFatalError = validatePrazoConclusaoComPrazoFatal(
            valor as DateValue,
            updated.data
          );
          
          if (prazoConclusaoComFatalError) {
            newErrors.prazo_para_conclusao = prazoConclusaoComFatalError;
          } else {
            delete newErrors.prazo_para_conclusao;
          }
          
          delete newErrors.data;
        } else if (campo === 'modalidade') {
          if ((valor === 'Virtual' || valor === 'online') && (!updated.endereco_virtual || updated.endereco_virtual.trim() === '')) {
            newErrors.endereco_virtual = 'Endereço virtual é obrigatório quando a modalidade é Virtual';
          } else {
            delete newErrors.endereco_virtual;
          }
        }
        
        return newErrors;
      });
      
      return updated;
    });
  }, [errors, demanda]);

  const hasChanges = useCallback((): boolean => {
    if (!dadosOriginais || !dadosEditados || !isEditing) {
      return false;
    }
    const isRestrito = isStatusFinalizadoOuCancelado(demanda);
    if (isRestrito) {
      const valorClienteOriginal = convertCurrencyToNumber(dadosOriginais.valor_cliente);
      const valorClienteEditado = convertCurrencyToNumber(dadosEditados.valor_cliente);
      const valorProfissionalOriginal = convertCurrencyToNumber(dadosOriginais.valor_profissional);
      const valorProfissionalEditado = convertCurrencyToNumber(dadosEditados.valor_profissional);
      
      return valorClienteOriginal !== valorClienteEditado || 
             valorProfissionalOriginal !== valorProfissionalEditado;
    }
    return hasChangesUtil(dadosOriginais, dadosEditados);
  }, [dadosOriginais, dadosEditados, isEditing, demanda]);

  const camposEditaveisRestritos = useMemo(() => {
    return isStatusFinalizadoOuCancelado(demanda);
  }, [demanda]);

  return {
    isEditing,
    dadosOriginais,
    dadosEditados: dadosEditados || demanda || null,
    iniciarEdicao,
    cancelarEdicao,
    salvarEdicao,
    hasChanges,
    atualizarDadosEditados,
    validateField,
    errors,
    isValidating,
    isSaving,
    camposEditaveisRestritos,
  };
}

